import { ChangeDetectionStrategy, Component, Type, effect, inject, signal, untracked } from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { TranslocoModule } from '@ngneat/transloco';
import { SupportAccessService } from '../support-access.service';

type SupportAdminListModule = typeof import('../support-admin-list.component');

/**
 * Beheerpagina Support (platform-management, eager). De beheerlijst komt via
 * een dynamische import zodat ze in de support-chunk blijft en niet met de
 * eager module meegebundeld wordt; dit component blijft daarom minimaal.
 */
@Component({
  selector: 'support-admin-page',
  imports: [NgComponentOutlet, TranslocoModule],
  templateUrl: './support-admin-page.component.html',
  styleUrl: './support-admin-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SupportAdminPageComponent {
  protected readonly access = inject(SupportAccessService);

  protected readonly list = signal<Type<unknown> | undefined>(undefined);
  protected readonly loadFailed = signal(false);

  /** Overschrijfbaar in specs; de echte import levert de lazy chunk. */
  protected loadList: () => Promise<SupportAdminListModule> = () => import('../support-admin-list.component');

  private loadStarted = false;

  constructor() {
    effect(() => {
      if (!this.access.canManage() || this.loadStarted) return;
      this.loadStarted = true;
      untracked(() => void this.ensureList());
    });
  }

  private async ensureList(): Promise<void> {
    try {
      const module = await this.loadList();
      this.list.set(module.SupportAdminListComponent);
    } catch {
      this.loadFailed.set(true);
    }
  }
}
